import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { TDeviceStatsSnapshot, TJsHeapStats, INativeDeviceStats, TStorageStats } from '@monorepo/types';
import { DeviceStats } from './device-stats';

/**
 * Chromium-only and non-standard, but present in the Android webview. Sizes are in bytes.
 * Kept local: it describes a browser API this service reads, not a shape anything else consumes.
 */
type TChromiumPerformanceMemory = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};

const BYTES_IN_MB = 1024 * 1024;

@Injectable({ providedIn: 'root' })
export class DeviceStatsService {
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  async getStatsSnapshot(): Promise<TDeviceStatsSnapshot> {
    return {
      native: await this.#getNativeStats(),
      jsHeap: this.#getJsHeapStats(),
      storage: await this.#getStorageStats(),
    };
  }

  /** Compact single line, so an entry stays greppable in the logging backend. */
  describeSnapshot({ native, jsHeap, storage }: TDeviceStatsSnapshot): string {
    const parts: string[] = [];

    if (native) {
      const { memory } = native;
      parts.push(
        `ram: ${memory.availableRamMb}/${memory.totalRamMb} MB free (kill<${memory.lowMemoryThresholdMb})`,
        `lowMemory: ${memory.isDeviceLowOnMemory}`,
        `appPss: ${memory.processTotalPssMb} MB`,
        `nativeHeap: ${memory.processNativeHeapMb ?? '-'} MB`,
      );
    } else {
      parts.push('native stats: unavailable');
    }

    parts.push(
      jsHeap ? `jsHeap: ${jsHeap.usedMb}/${jsHeap.limitMb} MB (${jsHeap.usedPercent}%)` : 'jsHeap: unavailable',
      storage ? `idb: ${storage.usedMb}/${storage.quotaMb} MB (${storage.usedPercent}%)` : 'idb: unavailable',
    );

    if (native) {
      const { cpu, power } = native;
      parts.push(
        `cpu: ${cpu.appCpuUsagePercent ?? '-'}%`,
        `thermal: ${cpu.thermalStatus ?? '-'}`,
        `battery: ${power.batteryPercent ?? '-'}%`,
        `powerSave: ${power.isPowerSaveMode ?? '-'}`,
      );
    }

    return parts.join(' | ');
  }

  async #getNativeStats(): Promise<INativeDeviceStats | null> {
    if (!this.isSupported()) return null;

    try {
      return await DeviceStats.getDeviceStats();
    } catch (error) {
      console.error('Unable to read native device stats:', error);
      return null;
    }
  }

  #getJsHeapStats(): TJsHeapStats | null {
    const memory = (performance as Performance & { memory?: TChromiumPerformanceMemory }).memory;
    if (!memory?.jsHeapSizeLimit) return null;

    return {
      usedMb: this.#toMb(memory.usedJSHeapSize),
      totalMb: this.#toMb(memory.totalJSHeapSize),
      limitMb: this.#toMb(memory.jsHeapSizeLimit),
      usedPercent: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100),
    };
  }

  /** Where the app's data actually sits, so growth on disk is visible next to growth in memory. */
  async #getStorageStats(): Promise<TStorageStats | null> {
    try {
      const { usage, quota } = await navigator.storage.estimate();
      if (!usage || !quota) return null;

      return {
        usedMb: this.#toMb(usage),
        quotaMb: this.#toMb(quota),
        usedPercent: Math.round((usage / quota) * 100),
      };
    } catch (error) {
      console.error('Unable to read storage estimate:', error);
      return null;
    }
  }

  #toMb(bytes: number): number {
    return Math.round((bytes / BYTES_IN_MB) * 100) / 100;
  }
}
